// Warstwa API modułu kompatybilności falownik↔magazyn (KONTRAKT §4 + ANEKS A5).
//
// JAK PODŁĄCZYĆ PRAWDZIWE BACKENDOWE API:
// 1. Ustaw zmienną środowiskową REACT_APP_COMPAT_API_URL na prefiks z kontraktu
//    (domyślnie backend deklaruje `/api/v1/compatibility`).
// 2. Zmień `USE_MOCK` poniżej na `false`.
// Kształt zwracanych danych (pola, enumy) jest już zgodny z §4/ANEKS — żaden
// komponent nie wymaga zmian przy przełączeniu.
import {
  MOCK_INVERTERS, MOCK_BATTERIES, resolveCheck,
} from './compatibilityMock';
import { zdarzenie } from '../lib/pomiar';
import { apiFetch } from './apiFetch';

const USE_MOCK = false;

const BASE_URL = process.env.REACT_APP_COMPAT_API_URL || '/api/v1/compatibility';
const MOCK_DELAY_MS = 220;

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const realFetch = async (path) => {
  const res = await apiFetch(`${BASE_URL}${path}`);
  if (!res.ok) throw new Error(`Compatibility API error ${res.status}`);
  return res.json();
};

const buildQuery = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, v);
  });
  return qs.toString();
};

const norm = (s) => (s || '').toLowerCase();

const paginate = (items, limit = 20, offset = 0) => items.slice(offset, offset + Number(limit || 20));

const inverterListItem = (i) => ({
  id: i.id, manufacturer: i.manufacturer, product_name: i.product_name, series_label: i.series_label,
  power_kw: i.power_kw, phase: i.phase, voltage_class: i.voltage_class, product_url: i.product_url,
  img_url: i.img_url ?? null,
});
const batteryListItem = (b) => ({
  id: b.id, manufacturer: b.manufacturer, product_name: b.product_name, family_label: b.family_label,
  capacity_kwh: b.capacity_kwh, voltage_class: b.voltage_class, product_url: b.product_url,
  img_url: b.img_url ?? null,
});

// ---- GET /inverters ----
export const searchInverters = async ({ q, manufacturer, battery_capable = true, limit = 20, offset = 0 } = {}) => {
  if (!USE_MOCK) return realFetch(`/inverters?${buildQuery({ q, manufacturer, battery_capable, limit, offset })}`);
  await delay(MOCK_DELAY_MS);
  let items = MOCK_INVERTERS;
  if (battery_capable === true || battery_capable === 'true') items = items.filter((i) => i.battery_capable);
  if (manufacturer) items = items.filter((i) => norm(i.manufacturer) === norm(manufacturer));
  if (q) items = items.filter((i) => norm(i.product_name).includes(norm(q)) || norm(i.manufacturer).includes(norm(q)));
  return { items: paginate(items, limit, offset).map(inverterListItem), total: items.length };
};

// ---- GET /batteries ----
export const searchBatteries = async ({ q, manufacturer, voltage_class, limit = 20, offset = 0 } = {}) => {
  if (!USE_MOCK) return realFetch(`/batteries?${buildQuery({ q, manufacturer, voltage_class, limit, offset })}`);
  await delay(MOCK_DELAY_MS);
  let items = MOCK_BATTERIES;
  if (manufacturer) items = items.filter((b) => norm(b.manufacturer) === norm(manufacturer));
  if (voltage_class) items = items.filter((b) => b.voltage_class === voltage_class);
  if (q) items = items.filter((b) => norm(b.product_name).includes(norm(q)) || norm(b.manufacturer).includes(norm(q)));
  return { items: paginate(items, limit, offset).map(batteryListItem), total: items.length };
};

// ---- GET /inverters/{id} · GET /batteries/{id} (ANEKS A5) ----
// Do hydratacji linku z jednym parametrem (np. ?inverter_id= bez battery_id=) —
// bez tego trzeba by ściągać całą listę i szukać po id, co nie skaluje się z katalogiem.
export const getInverterById = async (id) => {
  if (!USE_MOCK) return realFetch(`/inverters/${id}`);
  await delay(MOCK_DELAY_MS);
  const inv = MOCK_INVERTERS.find((i) => i.id === Number(id));
  return inv ? inverterListItem(inv) : null;
};

export const getBatteryById = async (id) => {
  if (!USE_MOCK) return realFetch(`/batteries/${id}`);
  await delay(MOCK_DELAY_MS);
  const bat = MOCK_BATTERIES.find((b) => b.id === Number(id));
  return bat ? batteryListItem(bat) : null;
};

// ---- GET /manufacturers?type= (ANEKS A5) ----
// Zasila filtr producenta — zastępuje wyprowadzanie listy z pełnego pobrania katalogu.
export const getManufacturers = async (type) => {
  if (!USE_MOCK) return realFetch(`/manufacturers?${buildQuery({ type })}`);
  await delay(MOCK_DELAY_MS);
  const source = type === 'battery' ? MOCK_BATTERIES : MOCK_INVERTERS.filter((i) => i.battery_capable);
  const counts = new Map();
  source.forEach((item) => counts.set(item.manufacturer, (counts.get(item.manufacturer) || 0) + 1));
  const items = [...counts.entries()].map(([name, product_count]) => ({ name, product_count })).sort((a, b) => a.name.localeCompare(b.name));
  return { items };
};

// ---- GET /check ----
export const checkCompatibility = async (inverterId, batteryId) => {
  if (!USE_MOCK) {
    const wynik = await realFetch(`/check?${buildQuery({ inverter_id: inverterId, battery_id: batteryId })}`);
    zdarzenie('kompatybilnosc_wynik', wynik?.user_status);
    return wynik;
  }
  await delay(MOCK_DELAY_MS);
  const result = resolveCheck(inverterId, batteryId);
  if (!result) throw new Error('Nie znaleziono urządzenia.');
  return result;
};

// Pięć kluczy zawsze obecnych, choćby pustych (ANEKS A3) — "incompatible" ma WŁASNY
// koszyk, osobny od "unknown" (mylenie "nie zadziała" z "nie wiemy" jest niedopuszczalne).
const EMPTY_GROUPS = { compatible: [], conditional: [], conflicting: [], incompatible: [], unknown: [] };
const EMPTY_COUNTS = { compatible: 0, conditional: 0, conflicting: 0, incompatible: 0, unknown: 0 };
const STATUS_SORT = ['compatible', 'conditional', 'conflicting', 'incompatible', 'unknown'];

const groupResults = (rows) => {
  const groups = { compatible: [], conditional: [], conflicting: [], incompatible: [], unknown: [] };
  const counts = { ...EMPTY_COUNTS };
  rows.forEach((row) => {
    const bucket = groups[row.user_status] ? row.user_status : 'unknown';
    counts[bucket] += 1;
    groups[bucket].push(row);
  });
  Object.keys(groups).forEach((k) => {
    groups[k].sort((a, b) => STATUS_SORT.indexOf(a.user_status) - STATUS_SORT.indexOf(b.user_status));
  });
  return { groups, counts };
};

// ---- GET /for-inverter/{inverter_id} ----
// `deviceManufacturer` NIE jest częścią kontraktu — to wyłącznie pomoc mocka: realny
// katalog ma inne ID niż fixture (1–7), więc bez tego KAŻDY prawdziwy produkt trafiał
// w "nie znaleziono" i pokazywał 0 wyników. Dopóki backend modułu kompatybilności nie
// działa na realnych ID, dopasowujemy reprezentatywne dane mockowe po producencie —
// żeby demo miało sens na prawdziwych stronach produktów. Zniknie przy USE_MOCK=false.
export const getCompatibleForInverter = async (inverterId, { status, manufacturer, limit = 50, offset = 0, deviceManufacturer } = {}) => {
  if (!USE_MOCK) return realFetch(`/for-inverter/${inverterId}?${buildQuery({ status, manufacturer, limit, offset })}`);
  await delay(MOCK_DELAY_MS);
  const inv = MOCK_INVERTERS.find((i) => i.id === Number(inverterId))
    || (deviceManufacturer && MOCK_INVERTERS.find((i) => i.battery_capable && norm(i.manufacturer) === norm(deviceManufacturer)));
  // Falownik spoza zbioru mockowego, bez dopasowania po producencie -> brak danych, nie błąd.
  if (!inv) return { inverter: null, groups: EMPTY_GROUPS, counts: EMPTY_COUNTS };
  let batteries = MOCK_BATTERIES;
  if (manufacturer) batteries = batteries.filter((b) => norm(b.manufacturer) === norm(manufacturer));
  const rows = batteries.map((b) => {
    const r = resolveCheck(inv.id, b.id);
    return {
      id: b.id, product_name: b.product_name, manufacturer: b.manufacturer, img_url: b.img_url ?? null,
      user_status: r.user_status, short_condition_pl: r.conditions?.[0]?.value_pl || null,
      product_url: b.product_url, offers_url: `/oferty/${b.id}`,
    };
  });
  const filtered = status ? rows.filter((r) => r.user_status === status) : rows;
  const { groups, counts } = groupResults(filtered);
  return { inverter: { id: inv.id, manufacturer: inv.manufacturer, product_name: inv.product_name }, groups, counts };
};

// ---- GET /for-battery/{battery_id} ----
// Patrz komentarz o `deviceManufacturer` przy getCompatibleForInverter — ten sam powód.
export const getCompatibleForBattery = async (batteryId, { status, manufacturer, limit = 50, offset = 0, deviceManufacturer } = {}) => {
  if (!USE_MOCK) return realFetch(`/for-battery/${batteryId}?${buildQuery({ status, manufacturer, limit, offset })}`);
  await delay(MOCK_DELAY_MS);
  const bat = MOCK_BATTERIES.find((b) => b.id === Number(batteryId))
    || (deviceManufacturer && MOCK_BATTERIES.find((b) => norm(b.manufacturer) === norm(deviceManufacturer)));
  // Magazyn spoza zbioru mockowego, bez dopasowania po producencie -> brak danych, nie błąd.
  if (!bat) return { battery: null, groups: EMPTY_GROUPS, counts: EMPTY_COUNTS };
  let inverters = MOCK_INVERTERS.filter((i) => i.battery_capable);
  if (manufacturer) inverters = inverters.filter((i) => norm(i.manufacturer) === norm(manufacturer));
  const rows = inverters.map((i) => {
    const r = resolveCheck(i.id, bat.id);
    return {
      id: i.id, product_name: i.product_name, manufacturer: i.manufacturer, img_url: i.img_url ?? null,
      user_status: r.user_status, short_condition_pl: r.conditions?.[0]?.value_pl || null,
      product_url: i.product_url, offers_url: `/oferty/${i.id}`,
    };
  });
  const filtered = status ? rows.filter((r) => r.user_status === status) : rows;
  const { groups, counts } = groupResults(filtered);
  return { battery: { id: bat.id, manufacturer: bat.manufacturer, product_name: bat.product_name }, groups, counts };
};

// ---- Endpointy modelowe (Kontekst 2) ----
// Działają na ID modelu katalogowego VoltValue (to samo `id`, którego strona modelu
// już używa w URL), nie na ID pojedynczej oferty jak funkcje wyżej. Zwracają tylko
// kandydatów: compatible/conditional/conflicting (+ unknown informacyjnie), bez
// "incompatible" — po stronie backendu, nie przez filtrowanie na froncie. Bez
// warstwy mock — dostępne wyłącznie na realnym API.
export const getCandidatesForInverterModel = async (modelId, { limit, offset } = {}) => realFetch(`/for-inverter-model/${modelId}?${buildQuery({ limit, offset })}`);

export const getCandidatesForMagazineModel = async (modelId, { limit, offset } = {}) => realFetch(`/for-magazine-model/${modelId}?${buildQuery({ limit, offset })}`);

// ---- GET /check-models ----
// Ten sam kształt odpowiedzi co /check (KONTRAKT §4) — różnica tylko w linkach
// (inverter.product_url/battery.product_url oraz offers.* wskazują na strony modeli
// /model/... zamiast /produkt/...), więc CompatibilityResultCard działa bez zmian.
export const checkModelsCompatibility = async (inverterModelId, magazineModelId) => {
  const result = await realFetch(`/check-models?${buildQuery({ inverter_model_id: inverterModelId, magazine_model_id: magazineModelId })}`);
  if (!result) throw new Error('Nie znaleziono modelu.');
  zdarzenie('kompatybilnosc_wynik', result.user_status);
  return result;
};

// ---- GET /pairs ----
// Spis stron par (/kompatybilnosc/<slug>, w Rumunii /ro/kompatybilnosc/<slug>) do listy linków
// pod narzędziem: { items: [{ slug, url, name, inverter_manufacturer, inverter_label,
// battery_manufacturer, battery_label, status }], total }. Backend trzyma go w cache 15 minut.
export const getPairs = async () => realFetch('/pairs');
